import React, { useEffect, useState, useMemo } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import { STAFFING_FREQ_MS, DAY_MS, CorpRoleView } from '@scale/shared/defaults';
import QMLoadingPage from 'corp/components/QM/QMLoadingPage';
import Dialog, { DialogContent, DialogActions } from 'react-scale/components/design-system/Dialog';
import GetRoleDescriptions from 'corp/components/CorpRoles/RoleDescriptions';
import { filterProjectNames } from 'corp/components/CorpRoles/helper';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { IProjectJson } from '@scale/shared/types/project';
import { Button, CircularProgress } from 'design-system';
import WorkCard from 'corp/components/CorpRoles/WorkCard';
import Confirmation from 'corp/components/CorpRoles/PopUpConfirmation';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { makeStyles } from 'design-system/styles';
import { IProjectRole, shouldShowRemoAdminProjectRole } from '@scale/shared/corp/projectRoles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProjectRoleModifyRequest } from '@scale/shared/qm/qmRequestTypes';
import ProjectRolesResultsPopup, {
  ProjectRoleResult,
} from 'corp/components/CorpRoles/ProjectRolesResultsPopup';
import _ from 'lodash';

export const STAFFING_KEY_PREFIX = 'local-store/staffing-status/';
const DEFAULT_DATE = '2000-01-01';
const WATERMARK_STAFFING_TIME_UTC = new Date(Date.UTC(2023, 1, 13, 20, 0));
export const LAST_UPDATE = 'last-update';
export const LAST_SNOOZE = 'snooze';

const filterOnlyActiveProjectRoles = (roles: IProjectRole[]) => {
  const activeProjectRoles = roles.filter(
    (pr: IProjectRole) =>
      new Date(pr.startDate) <= new Date() &&
      (!pr.stopDate || new Date(pr.stopDate) > new Date()) &&
      shouldShowRemoAdminProjectRole(pr),
  );

  return activeProjectRoles;
};

const getActiveSuggestedToEndProjectRolesOfUser = async (userId: string) => {
  const [rolesOfUser, rolesSuggestedToEnd, projectsWithActivity] = await Promise.all([
    apiFetch(null, `corp-api/qm/project_roles/?user=${userId}`, {
      method: 'GET',
    }),
    apiFetch(null, `corp-api/qm/project_roles/suggested_end`, {
      method: 'GET',
    }),
    apiFetch(null, `corp-api/qm/project_roles/activity/?user=${userId}`, {
      method: 'GET',
    }),
  ]);

  const rolesSuggestedToEndOfUser = rolesOfUser.filter((projectRole: IProjectRole) =>
    rolesSuggestedToEnd.find(
      (pr: ISuggestedToEndProjectRoleSnowflake) => pr.project_role_id === projectRole._id!,
    ),
  );

  const activeSuggestedToEndProjectRoles = filterOnlyActiveProjectRoles(
    rolesSuggestedToEndOfUser,
  ).filter(projectRole => !projectsWithActivity.includes(projectRole.project.toString()));

  return activeSuggestedToEndProjectRoles;
};

interface IReminderProp {
  user: IUserJson;
}

export const UpdateStaffing = ({ user }: IReminderProp) => {
  const [showModal, setShowModal] = useState(false);
  const [snoozed, setSnoozed] = useState(false);
  const path = typeof window !== 'undefined' && window.location.pathname;
  const showStaffingReminder = useUnleashFeatureFlag(
    'show_staffing_reminder',
    { userId: user.id },
    false,
  );

  async function fetchData() {
    const activeSuggestedToEndProjectRoles = await getActiveSuggestedToEndProjectRolesOfUser(
      user.id,
    );

    if (activeSuggestedToEndProjectRoles.length === 0) {
      return;
    }
    setShowModal(true);
  }

  useEffect(() => {
    (async () => {
      if (showStaffingReminder) {
        const snoozed =
          localStorage.getItem(STAFFING_KEY_PREFIX + LAST_SNOOZE) !== null
            ? localStorage.getItem(STAFFING_KEY_PREFIX + LAST_SNOOZE)
            : null;

        const lastUserManuallyUpdate = user.lastStaffingUpdateTime
          ? new Date(user.lastStaffingUpdateTime)
          : new Date(DEFAULT_DATE);
        const forceShowStaffingWatermark =
          lastUserManuallyUpdate.getTime() < WATERMARK_STAFFING_TIME_UTC.getTime();

        // if the user clicked dismiss on the first time they saw popup, don't show it immediately
        if ((!user.lastStaffingUpdateTime && !snoozed) || forceShowStaffingWatermark) {
          setShowModal(true);
        }

        if (user.lastStaffingUpdateTime) {
          const msSinceUpdate =
            new Date().getTime() - new Date(user.lastStaffingUpdateTime).getTime();
          if (msSinceUpdate < STAFFING_FREQ_MS) return;
        }

        // check local storage for time last updated
        const lastStaffing =
          localStorage.getItem(STAFFING_KEY_PREFIX + LAST_UPDATE) !== null
            ? localStorage.getItem(STAFFING_KEY_PREFIX + LAST_UPDATE)!
            : DEFAULT_DATE;

        const lastChecked = new Date(lastStaffing).getTime();
        const currentTime = new Date().getTime();

        const activeSuggestedToEndProjectRoles = await getActiveSuggestedToEndProjectRolesOfUser(
          user.id,
        );

        if (activeSuggestedToEndProjectRoles.length === 0) {
          return;
        }

        if (currentTime - lastChecked < DAY_MS) {
          return;
        }

        if (!user.lastStaffingUpdateTime) {
          setShowModal(true);
        }

        if (snoozed === 'true') {
          setSnoozed(true);
        }

        if (!showModal) {
          fetchData();
        }
      }
    })();
  }, [showStaffingReminder, path, setSnoozed, user, snoozed]);

  if (!showModal) return <div></div>;
  return <ReminderModal user={user} snoozed={snoozed} show={showModal} setShow={setShowModal} />;
};

interface IProp {
  user: IUserJson;
  snoozed: boolean;
  show: boolean;
  setShow: (show: boolean) => void;
}

interface ISuggestedToEndProjectRoleSnowflake {
  cnt: number;
  email: string;
  project: string;
  project_id: string;
  project_role_id: string;
  title: string;
  role: string;
}

const ReminderModal = ({ user, snoozed, show, setShow }: IProp) => {
  const styles = useStyles();
  const [showSuggestedEndProject, setShowSuggestedEndProject] = useState<Map<String, boolean>>(
    new Map(),
  );
  const [projectRolesSuggestedToEnd, setProjectRolesSuggestedToEnd] = useState<IProjectRole[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDescriptions, setShowDescriptions] = useState<boolean>(false);
  const [counter, setCounter] = useState(0);
  const [suggestedToEndProjectsBeingEnded, setSuggestedToEndProjectsBeingEnded] = useState<
    string[]
  >([]);
  const [suggestedToEndProjectRolesBeingEnded, setSuggestedToEndProjectRolesBeingEnded] = useState<
    string[]
  >([]);
  const [projectRoleOperationsResults, setProjectRoleOperationsResults] = useState<
    ProjectRoleResult[]
  >([]);
  const [showResultsPopup, setShowResultsPopup] = useState<boolean>(false);

  const [selectedProjectRolesIds, setSelectedProjectRolesIds] = useState<string[]>([]);
  const [confirmedProjectRolesIds, setConfirmedProjectRolesIds] = useState<string[]>([]);

  useEffect(() => {
    async function fetchData() {
      setLoading(counter === 0);

      const activeSuggestedToEndProjectRoles = await getActiveSuggestedToEndProjectRolesOfUser(
        user.id,
      );

      setShowSuggestedEndProject(showSuggestedEndProject);
      setProjectRolesSuggestedToEnd(activeSuggestedToEndProjectRoles);
      setLoading(false);
      setShow(show);
    }

    fetchData();
    return;
  }, [user, counter, show]);

  useEffect(() => {
    setShowResultsPopup(projectRoleOperationsResults.length > 0);
  }, [projectRoleOperationsResults]);

  const activeProjectRolesSuggestedToEnd = useMemo(() => {
    return projectRolesSuggestedToEnd.filter(
      (pr: IProjectRole) =>
        new Date(pr.startDate) <= new Date() &&
        (!pr.stopDate || new Date(pr.stopDate) > new Date()) &&
        shouldShowRemoAdminProjectRole(pr),
    );
  }, [projectRolesSuggestedToEnd]);

  const allRolesConfirmed = useMemo(
    () => activeProjectRolesSuggestedToEnd.every(pr => confirmedProjectRolesIds.includes(pr._id!)),
    [activeProjectRolesSuggestedToEnd, confirmedProjectRolesIds],
  );

  const stopRoles = async (projectRolesToRemove: ProjectRoleModifyRequest) => {
    if (projectRolesToRemove.length > 0) {
      try {
        const modifyRolesResults = await apiFetch<(IProjectRole | Error)[]>(
          null,
          `corp-api/qm/project_role_events/`,
          {
            method: 'PATCH',
            body: projectRolesToRemove,
          },
        );
        setProjectRoleOperationsResults(
          modifyRolesResults.map(result => ({
            error: !!(result as Error).message,
            errorMessage: (result as Error).message || '',
            result,
          })),
        );

        setCounter(x => x + 1);
      } catch (error) {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
      }
    }
  };

  function handleSuggestedEndProject(project: String, show: boolean) {
    showSuggestedEndProject.set(project, show);
    setCounter(x => x + 1);
  }

  const assignedProjectsSuggestedToEnd = filterProjectNames(
    activeProjectRolesSuggestedToEnd,
    true,
    false,
  );

  return (
    <div>
      {loading && (
        <div>
          <Dialog open={show} onClose={() => setShow(false)}>
            <DialogContent>
              <div className={styles.projectWrapper}>
                <QMLoadingPage />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {!loading && (
        <div>
          <Dialog
            open={show}
            disableEscapeKeyDown={true}
            disableBackdropClick={true}
            onClose={() => setShow(false)}
          >
            <DialogContent>
              {!!selectedProjectRolesIds.length && !suggestedToEndProjectRolesBeingEnded.length && (
                <div className={styles.batchActions}>
                  <div
                    className="batch-confirm-button"
                    onClick={() => {
                      setConfirmedProjectRolesIds(prev =>
                        _.uniq([...prev, ...selectedProjectRolesIds]),
                      );
                    }}
                  >
                    <FontAwesomeIcon icon="check" />
                    CONFIRM SELECTED
                  </div>
                  <div
                    className="batch-end-button"
                    onClick={async () => {
                      setSuggestedToEndProjectRolesBeingEnded(prev =>
                        _.uniq([...prev, ...selectedProjectRolesIds]),
                      );

                      await stopRoles(
                        activeProjectRolesSuggestedToEnd
                          .filter((pr: IProjectRole) => selectedProjectRolesIds.includes(pr._id!))
                          .map((pr: IProjectRole) => ({
                            projectRoleId: pr._id!,
                            newStartDate: pr.startDate,
                            newEndDate: new Date(),
                          })),
                      );

                      setSelectedProjectRolesIds(prev =>
                        prev.filter(
                          prevProjectRoleId => !selectedProjectRolesIds.includes(prevProjectRoleId),
                        ),
                      );
                      setSuggestedToEndProjectRolesBeingEnded(prev =>
                        prev.filter(
                          prevProjectRoleId => !selectedProjectRolesIds.includes(prevProjectRoleId),
                        ),
                      );
                    }}
                  >
                    <FontAwesomeIcon icon="times-circle" />
                    END SELECTED
                  </div>
                </div>
              )}
              <div>
                <div className={styles.projectWrapper}>
                  <div className={styles.title}>
                    <div>Update Staffing Information</div>
                  </div>

                  <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                    <div>
                      For more information, please see the guide{' '}
                      <a href="https://scale.atlassian.net/wiki/spaces/OP/pages/106627097/Project+Roles+and+Corp+Profiles">
                        here
                      </a>{' '}
                      and FAQ{' '}
                      <a href="https://scale.atlassian.net/wiki/spaces/OP/pages/106496091/Project+Roles+FAQ">
                        here.
                      </a>{' '}
                      <div
                        className={styles.showText}
                        onClick={() => setShowDescriptions(!showDescriptions)}
                      >
                        {showDescriptions ? (
                          <div className={styles.hideText}>
                            <b> Hide Role Descriptions </b>
                          </div>
                        ) : (
                          <div>
                            <b> Show Role Descriptions </b>
                          </div>
                        )}
                      </div>
                    </div>
                    {showDescriptions && <GetRoleDescriptions />}
                  </div>

                  <div style={{ height: '450px', overflowY: 'scroll' }}>
                    {assignedProjectsSuggestedToEnd &&
                      assignedProjectsSuggestedToEnd.length > 0 && (
                        <div className={styles.sections}>
                          <div className={styles.roleSectionTitle}>Confirm or End Roles</div>
                          <div className={styles.roleSectionSubtitle} style={{}}>
                            These roles appear inactive based on recent activity
                          </div>
                          <div>
                            {assignedProjectsSuggestedToEnd.map(project => (
                              <div
                                key={project}
                                className={`${styles.roleSection} ${
                                  suggestedToEndProjectsBeingEnded.find(
                                    suggestedToEndProjectBeingEndedName =>
                                      suggestedToEndProjectBeingEndedName === project,
                                  ) && styles.projectBeingEnded
                                }`}
                              >
                                {suggestedToEndProjectsBeingEnded.find(
                                  suggestedToEndProjectBeingEndedName =>
                                    suggestedToEndProjectBeingEndedName === project,
                                ) && (
                                  <div className={styles.projectBeingEndedLoadingView}>
                                    <CircularProgress
                                      size="24px"
                                      color="inherit"
                                      style={{ zIndex: '2' }}
                                    />
                                  </div>
                                )}
                                <div className={styles.roleTitle}>{project}</div>
                                <div
                                  className={styles.remove}
                                  onClick={() => handleSuggestedEndProject(project, true)}
                                >
                                  {' '}
                                  End all my inactive roles on this project
                                </div>
                                <Dialog
                                  open={showSuggestedEndProject.get(project)!}
                                  onClose={() => handleSuggestedEndProject(project, false)}
                                >
                                  <DialogContent style={{ justifyContent: 'center' }}>
                                    Are you sure you want to end all your inactive roles on{' '}
                                    <b>{project}</b>?<br></br>
                                    The end date for each role will be set to today.
                                  </DialogContent>
                                  <DialogActions style={{ justifyContent: 'center' }}>
                                    <Button
                                      onClick={() => handleSuggestedEndProject(project, false)}
                                    >
                                      No
                                    </Button>
                                    <Button
                                      type="primary"
                                      onClick={async () => {
                                        setSuggestedToEndProjectsBeingEnded(current => [
                                          ...current,
                                          project,
                                        ]);

                                        handleSuggestedEndProject(project, false);

                                        await stopRoles(
                                          activeProjectRolesSuggestedToEnd
                                            .filter(
                                              (pr: IProjectRole) =>
                                                (pr.project as IProjectJson).name === project,
                                            )
                                            .map((pr: IProjectRole) => ({
                                              projectRoleId: pr._id!,
                                              newStartDate: pr.startDate,
                                              newEndDate: new Date(),
                                            })),
                                        );

                                        setSuggestedToEndProjectsBeingEnded(current =>
                                          current.filter(projectName => projectName !== project),
                                        );
                                      }}
                                    >
                                      Yes
                                    </Button>
                                  </DialogActions>
                                </Dialog>
                                <div className={styles.names_section}>
                                  {activeProjectRolesSuggestedToEnd
                                    .filter(
                                      (role: IProjectRole) =>
                                        (role.project as IProjectJson).name === project,
                                    )
                                    .map((role: IProjectRole, index: number) => (
                                      <WorkCard
                                        key={index}
                                        user={user}
                                        role={role}
                                        title={role.role}
                                        view={CorpRoleView.Role}
                                        roleText={role.role}
                                        showLoadingView={suggestedToEndProjectRolesBeingEnded.includes(
                                          String(role._id),
                                        )}
                                        buttonLabels={[
                                          confirmedProjectRolesIds.find(prid => prid === role._id)
                                            ? 'Confirmed!'
                                            : 'Confirm this role',
                                          'End this role',
                                        ]}
                                        buttonActions={[
                                          projectRoleToConfirm => {
                                            setConfirmedProjectRolesIds(prev =>
                                              _.uniq([...prev, projectRoleToConfirm._id!]),
                                            );
                                          },
                                          async (projectRoleToEnd: IProjectRole) => {
                                            setSuggestedToEndProjectRolesBeingEnded(prev => [
                                              ...prev,
                                              projectRoleToEnd._id || projectRoleToEnd.id || '',
                                            ]);
                                            await stopRoles(
                                              activeProjectRolesSuggestedToEnd
                                                .filter(
                                                  (pr: IProjectRole) =>
                                                    pr._id === projectRoleToEnd._id,
                                                )
                                                .map((pr: IProjectRole) => ({
                                                  projectRoleId: pr._id!,
                                                  newStartDate: pr.startDate,
                                                  newEndDate: new Date(),
                                                })),
                                            );
                                            setSuggestedToEndProjectRolesBeingEnded(prev =>
                                              prev.filter(
                                                prevProjectRoleId =>
                                                  prevProjectRoleId !== projectRoleToEnd._id,
                                              ),
                                            );
                                          },
                                        ]}
                                        checkboxSelected={
                                          !!selectedProjectRolesIds.find(prid => prid === role._id)
                                        }
                                        checkboxAction={() => {
                                          if (
                                            selectedProjectRolesIds.find(prid => prid === role._id)
                                          ) {
                                            setSelectedProjectRolesIds(prev =>
                                              prev.filter(prid => prid !== role._id),
                                            );
                                          } else {
                                            setSelectedProjectRolesIds(prev => [
                                              ...prev,
                                              role._id!,
                                            ]);
                                          }
                                        }}
                                      />
                                    ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                <Confirmation
                  user={user}
                  setShow={setShow}
                  snoozed={snoozed}
                  setPopupCounter={setCounter}
                  canClose={allRolesConfirmed}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <ProjectRolesResultsPopup
        isModalOpen={showResultsPopup}
        hideModal={() => setShowResultsPopup(false)}
        title="End Roles: Results"
        results={projectRoleOperationsResults}
      />
    </div>
  );
};

const useStyles = makeStyles({
  projectBeingEnded: {
    position: 'relative',
    opacity: '0.5',
  },
  projectBeingEndedLoadingView: {
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'black',
  },
  remove: {
    fontSize: '13px',
    padding: '4px 8px',
    alignItems: 'center',
    border: 'var(--color-RemoGray20) solid 1px',
    borderRadius: 4,
    cursor: 'pointer',
    background: 'white',
    color: 'var(--color-RemoGray50)',
    position: 'absolute',
    right: '14px',
    bottom: '-14px',
    '&:hover': {
      backgroundColor: '#F2F2F2',
      color: '#8B0000',
      borderRadius: 4,
    },
  },
  title: {
    fontSize: '25px',
    fontWeight: 500,
    justifyContent: 'center',
    margin: 10,
  },
  projectWrapper: {
    width: '600px',
    background: 'white',
  },
  names_section: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: '5px',
    alignItems: 'flex-end',
  },
  sections: {
    margin: '10px',
    borderRadius: '4px',
    padding: '10px',
    background: 'var(--color-RemoGray01)',
    display: 'flex',
    flexDirection: 'column',
  },
  roleTitle: {
    fontSize: '15px',
    fontWeight: 500,
    paddingTop: '3px',
    paddingLeft: '5px',
  },
  roleSection: {
    padding: '15px',
    marginBottom: '20px',
    margin: '15px',
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    background: 'white',
    border: 'var(--color-RemoGray20) solid 1px',
    borderRadius: 4,
    position: 'relative',
  },
  roleSectionTitle: {
    width: '100%',
    fontSize: '20px',
    fontWeight: 500,
    paddingLeft: '15px',
    paddingRight: '5px',
  },
  roleSectionSubtitle: {
    width: '100%',
    fontSize: '12px',
    padding: '0 5px 0 15px',
  },
  hideText: {
    color: '#8B0000',
    '&:hover': {
      color: '#be0000',
    },
  },
  showText: {
    cursor: 'pointer',
    color: '#169016',
    marginBottom: 10,
    '&:hover': {
      color: '#19a619',
    },
  },
  batchActions: {
    width: '120px',
    position: 'absolute',
    top: '50%',
    right: '0',
    zIndex: 2,
    '& > div': {
      padding: '5px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '10px',
      color: 'white',
      cursor: 'pointer',
      '&.batch-confirm-button': {
        backgroundColor: 'var(--color-RemoBlue)',
        '&:hover': {
          backgroundColor: '#57b9f1',
        },
      },
      '&.batch-end-button': {
        backgroundColor: 'var(--color-RemoRed)',
        '&:hover': {
          backgroundColor: '#ee7c6c',
        },
      },
      '& svg': {
        fontSize: '20px',
      },
    },
  },
});
